// What the pre-upsell says, and why it says it to this user.
//
// Two axes, no more: the GOAL picks the room on screen (rooms.ts), and the
// second question picks the headline. Everything else on the screen is
// fixed. Resist adding a third axis — 5 × 5 wordings is a maintenance job
// nobody will keep honest.
//
// `personalise` is the ORIGINAL engine, keyed on the acoustic-concern
// question (bass/echo/mixes/soundstage/unsure) — the onboarding wizard no
// longer asks that question, but the homepage's listening room (through
// landing/showcase/showcaseTabs.ts) and the /design/a|b|c review harness
// still drive fixed example combinations through it, so it stays. `personaliseByDesignMethod`, below `personalise`,
// is what the real onboarding pre-upsell actually calls now: the question
// changed to "how have you been designing this", which has no honest
// curve-worth-drawing of its own, so PreUpsell.tsx picks that at random
// instead (see its own note) and only the headline is personalised here.
//
// The headline is a plain promise with no number in it. The number lives in
// the chart's readout, where a before→after pair reads as a measurement
// rather than as a claim: "16 dB → 6 dB" is something a reader can anchor on
// precisely because the sentence above it did not try to sell them on it.

import type { DesignMethodId, PlacementProblemId } from '@roomtreatment/room-flow';
import type { RoomCurves } from './rooms';
import { TESTIMONIALS } from '@/lib/copy/testimonials';

/** Which curve actually answers the problem. Frequency response covers the
 *  modal region, decay covers 40 Hz–10 kHz — drawing the wrong one is a lie
 *  told in pictures. */
export type CurveKind = 'freq' | 'rt60';

export interface Personalised {
  /** The solution, as a plain promise. The only sentence on the screen. */
  headline: string;
  /** The measured change, rendered beside the curve it is read off. */
  figure: { label: string; before: string; after: string };
  curve: CurveKind;
}

type Unit = 'dB' | 's' | '±dB';

// Soundstage borrows the decay figure: early decay is what smears an image,
// and it is the only imaging-relevant number the snapshots carry today.
// `soundstage.decay_symmetry_pct` is the honest metric for it — wire it up
// here once designed rooms make it move (it is already extracted).
const SPEC: Record<PlacementProblemId, {
  headline: string;
  curve: CurveKind;
  metric: keyof RoomCurves['metrics'];
  label: string;
  unit: Unit;
}> = {
  bass: {
    headline: 'Even out your bass.',
    curve: 'freq', metric: 'bassSwingDb', label: 'Bass swing', unit: 'dB',
  },
  echo: {
    headline: 'Stop the echo.',
    curve: 'rt60', metric: 'decayS', label: 'Decay', unit: 's',
  },
  mixes: {
    headline: 'Make your mixes translate.',
    curve: 'freq', metric: 'windowDb', label: 'Response window', unit: '±dB',
  },
  soundstage: {
    headline: 'Rebuild the stereo image.',
    curve: 'rt60', metric: 'decayS', label: 'Decay', unit: 's',
  },
  unsure: {
    headline: 'Make it sound right.',
    curve: 'freq', metric: 'windowDb', label: 'Response window', unit: '±dB',
  },
};

function fmt(value: number, unit: Unit): string {
  if (unit === 's') return `${value.toFixed(2).replace(/0$/, '')} s`;
  if (unit === '±dB') return `±${Math.round(value)} dB`;
  return `${Math.round(value)} dB`;
}

export function personalise(problem: PlacementProblemId | null, curves: RoomCurves): Personalised {
  const spec = SPEC[problem ?? 'unsure'];
  const m = curves.metrics[spec.metric];
  const figure = { label: spec.label, before: fmt(m.before, spec.unit), after: fmt(m.after, spec.unit) };
  return { headline: spec.headline, figure, curve: spec.curve };
}

// ── The real onboarding question: "how have you been designing this?" ────
//
// Multi-select, so more than one answer can come back. Each carries a
// headline AND a rank — the lower the rank, the more it has to say — and
// only the best-ranked pick among the ones the visitor chose gets shown.
// Picking "pen and paper" and "advice online" together shows "Pen and paper
// isn't enough": rank 1 beats rank 5 regardless of click order.
//
// This no longer picks the curve — bass/echo/mixes/soundstage isn't a fact
// this question collects, so there is nothing honest to key the curve off.
// PreUpsell.tsx randomises it once per mount instead (see its own note) and
// hands the result in here just to read the matching figure off it.
const METHOD_HEADLINE: Record<DesignMethodId, { headline: string; rank: number }> = {
  'pen-paper': { headline: "Pen and paper isn't enough.", rank: 1 },
  'no-tools': { headline: 'Stop trying to visualize.', rank: 2 },
  consultant: { headline: "Use the consultant's toolkit.", rank: 3 },
  '3d-software': { headline: 'Use the right tools.', rank: 4 },
  'advice-online': { headline: 'Get real advice.', rank: 5 },
};

/** The figure to read off whichever curve was randomly chosen — 'freq' has
 *  no single problem-derived metric to fall back on any more, so this uses
 *  the same generic pairing `personalise` gives 'mixes' and 'unsure' above. */
const CURVE_METRIC: Record<CurveKind, { metric: keyof RoomCurves['metrics']; label: string; unit: Unit }> = {
  freq: { metric: 'windowDb', label: 'Response window', unit: '±dB' },
  rt60: { metric: 'decayS', label: 'Decay', unit: 's' },
};

/** The headline for a set of design-method picks: whichever selected option
 *  has the lowest (most powerful) rank. Empty selection (nothing answered
 *  yet, an advanced-rail caller that never asked) falls back to the
 *  "no tools" phrasing — closest in spirit to an unanswered "just want it to
 *  look right". */
export function headlineForDesignMethods(methods: DesignMethodId[]): string {
  if (methods.length === 0) return METHOD_HEADLINE['no-tools'].headline;
  const best = methods.reduce((a, b) => (METHOD_HEADLINE[b].rank < METHOD_HEADLINE[a].rank ? b : a));
  return METHOD_HEADLINE[best].headline;
}

export function personaliseByDesignMethod(
  methods: DesignMethodId[],
  curve: CurveKind,
  curves: RoomCurves,
): Personalised {
  const { metric, label, unit } = CURVE_METRIC[curve];
  const m = curves.metrics[metric];
  const figure = { label, before: fmt(m.before, unit), after: fmt(m.after, unit) };
  return { headline: headlineForDesignMethods(methods), figure, curve };
}

/** Three short, real quotes for the pre-upsell's tail strip. A visitor who
 *  reaches the pre-upsell has just seen their OWN before/after; proof that
 *  someone else got real value out of the result lands better here than
 *  three more claims about what the product does. */
export const TESTIMONIAL_HIGHLIGHTS: { short: string; author: string }[] = TESTIMONIALS.map(
  (t) => ({ short: t.short, author: t.author }),
);
